import moment from "moment";

const trafficDeviations = {
    namespaced: true,
    state: {
        dateList: [],
        selectedDate: '',
        selectedTripId: '',
        cancellationsToAdd: {},
        cancellationsToRemove: {},
        tripCancellationTimestamp: new moment(),

        courseAndVehicleList: [],
        tripsByCourse: {},
        selectedCourseNumber: '',
        selectedCourseWithVehicles: {},

        blockList: [],
        tripsByBlockSequence: {},
        selectedBlockSequence: {}
    },
    mutations: {
        updateDateList(state, newList) {
            state.dateList = newList;
        },
        updateSelectedDate(state, date) {
            // Information already fetched has to be wiped, because courses and trips are not unique across dates.
            state.courseAndVehicleList = [];
            state.tripsByCourse = {};
            state.selectedCourseNumber = '';
            state.selectedCourseWithVehicles = {};

            state.blockList = [];
            state.tripsByBlockSequence = {};
            state.selectedBlockSequence = {};

            state.selectedTripId = '';
            state.cancellationsToAdd = {};
            state.cancellationsToRemove = {};

            state.selectedDate = date;
        },
        updateSelectedTripId(state, newData) {
            state.selectedTripId = newData;
        },
        putCancellationToAdd(state, newData) {
            state.cancellationsToAdd[newData.key] = newData.passing;
        },
        putCancellationToRemove(state, newData) {
            state.cancellationsToRemove[newData.key] = newData.passing;
        },
        deleteCancellationToAdd(state, key) {
            delete state.cancellationsToAdd[key] ;
        },
        deleteCancellationToRemove(state, key) {
            delete state.cancellationsToRemove[key];
        },
        putTripCancellationToAdd(state, tripId) {
            let trip = getTripById(state, tripId);
            if (!trip)
                return;
            for (const passing of trip.passings) {
                state.cancellationsToAdd[getPassingKey(passing)] = passing;
            }
        },
        putTripCancellationToRemove(state, tripId) {
            let trip = getTripById(state, tripId);
            if (!trip)
                return;
            for (const passing of trip.passings) {
                state.cancellationsToRemove[getPassingKey(passing)] = passing;
            }
        },
        deleteTripCancellationToAdd(state, tripId) {
            let trip = getTripById(state, tripId);
            if (!trip)
                return;
            for (const passing of trip.passings) {
                delete state.cancellationsToAdd[getPassingKey(passing)];
            }
        },
        deleteTripCancellationToRemove(state, tripId) {
            let trip = getTripById(state, tripId);
            if (!trip)
                return;
            for (const passing of trip.passings) {
                delete state.cancellationsToRemove[getPassingKey(passing)];
            }
        },
        updateTripCancellationTimestamp(state) {
            state.tripCancellationTimestamp = new moment();
        },

        updateCourseAndVehicleList(state, newList) {
            state.courseAndVehicleList = newList;
        },
        updateTripsByCourse(state, newData) {
            state.tripsByCourse[newData.course] = newData.newList;
            state.tripsByCourse = { ...state.tripsByCourse };
        },
        updateSelectedCourseNumber(state, newData) {
            state.selectedCourseNumber = newData;
        },
        updateSelectedCourseWithVehicles(state, newData) {
            state.selectedCourseWithVehicles = newData;
        },
        
        updateBlockList(state, newList) {
            state.blockList = newList;
        },
        updateTripsByBlockSequence(state, newData) {
            state.tripsByBlockSequence[newData.blockSequenceId] = newData.newList;
            state.tripsByBlockSequence = { ...state.tripsByBlockSequence };
        },
        updateSelectedBlockSequence(state, newData) {
            state.selectedBlockSequence = newData;
        }
    },
    actions: {
        getDateList(context) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.get('/deviations/rest/api/get-date-list')
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateDateList', response.data);
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        getCourseAndVehicleList(context, date) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.get('/deviations/rest/api/get-course-list-with-vehicles', { params: { "date": date }})
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateCourseAndVehicleList', response.data);
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        getTripsByCourseAndDate(context, parameters) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.get('/deviations/rest/api/get-trips-by-course-and-date', { params: {
                        course: parameters.course,
                        date: parameters.date
                    }})
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateTripsByCourse', { course: parameters.course, newList: response.data });
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        getBlockList(context, date) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.get('/deviations/rest/api/get-blocks-for-date', { params: { "date": date }})
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateBlockList', response.data);
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        getTripsByBlockSequenceAndDate(context, parameters) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.get('/deviations/rest/api/get-trips-by-block-sequence-and-date', { params: {
                        block_sequence: parameters.blockSequenceId,
                        date: parameters.date
                    }})
                    .then(response => {
                        if (response.status == 200) {
                            context.commit('updateTripsByBlockSequence', { blockSequenceId: parameters.blockSequenceId, newList: response.data });
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        addCanceledDepartures(context, departureList) {
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.post('/deviations/rest/api/add-canceled-departures', departureList)
                    .then(response => {
                        if (response.status == 200) {
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        },
        removeCanceledDepartures(context, departureList) {
            // Departures are identified and thus are required to include: lineId, routeId, stopId, departureTime and passingTime.
            return new Promise((resolve, reject) => {
                this._vm.$traveller_http.post('/deviations/rest/api/remove-canceled-departures', departureList)
                    .then(response => {
                        if (response.status == 200) {
                            resolve(response);
                        } else {
                            reject();
                        }
                    })
                    .catch(error => {
                        reject(error);
                    })
            });
        }
    }
}

export default trafficDeviations;

function getTripById(state, tripId) {
    let tripsList = [];
    
    let blockSequenceId = state.selectedBlockSequence.blockSequenceId;
    if (blockSequenceId) {
        tripsList = state.tripsByBlockSequence[blockSequenceId];
    } else {
        let courseNumber = state.selectedCourseWithVehicles.courseNumber;
        tripsList = state.tripsByCourse[courseNumber];
    }
    if (Array.isArray(tripsList) && tripsList.length > 0) {
        let matchingTrips = tripsList.filter(trip => trip.tripId == tripId);
        if (Array.isArray(matchingTrips) && matchingTrips.length > 0) {
            return matchingTrips[0];
        }
    }
    return null;
}

export {
    getPassingKey,
    isPassingCanceled,
    haveCancellationsBeenModified
}

function getPassingKey(passing) {
    return passing.stopOrder +'##'+ passing.tripId +'##'+ passing.departureTime;
}

function isPassingCanceled(passing) {
    switch (passing.cancelingStatus) {
        case 'canceling_added':
        case 'canceling_delivered':
        case 'canceling_verified':
            return true;
        default:
            return false;
    }
}

function haveCancellationsBeenModified() {
    const cancellationsToAdd = trafficDeviations.state.cancellationsToAdd;
    if (cancellationsToAdd) {
        for (const key in cancellationsToAdd) {
            if (cancellationsToAdd.hasOwnProperty(key)) {
                return true;
            }
        }
    }
    const cancellationsToRemove = trafficDeviations.state.cancellationsToRemove;
    if (cancellationsToRemove) {
        for (const key in cancellationsToRemove) {
            if (cancellationsToRemove.hasOwnProperty(key)) {
                return true;
            }
        }
    }
    return false;
}
